"use client"

import { useState, useRef, useEffect } from "react"
import { useRouter } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import { Loader2, Delete, KeyRound, RotateCcw, Power } from "lucide-react"

const APP_VERSION = "1.17.0"

export default function LoginPage() {
  const router = useRouter()
  const [mode, setMode] = useState<"pin" | "email">("pin")

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B3D5E]">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/logo.svg" alt="VUX" className="h-14 object-contain brightness-0 invert" />
        </div>

        {mode === "pin" ? (
          <PinLogin onSwitchToEmail={() => setMode("email")} />
        ) : (
          <EmailLogin onSwitchToPin={() => setMode("pin")} />
        )}
      </div>
    </div>
  )
}

// ── PIN Login ────────────────────────────────────────────────────────────────

function PinLogin({ onSwitchToEmail }: { onSwitchToEmail: () => void }) {
  const router = useRouter()
  const [pin, setPin] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const maxLength = 6

  // Auto-submit bei exakt 6 Stellen
  useEffect(() => {
    if (pin.length !== 6) return
    handlePinLogin()
  }, [pin])

  // Physische Tastatur: Ziffern, Backspace, Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (loading) return
      if (/^\d$/.test(e.key)) { e.preventDefault(); addDigit(e.key) }
      else if (e.key === "Backspace") { e.preventDefault(); removeDigit() }
      else if (e.key === "Escape") { e.preventDefault(); clearPin() }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [loading, pin])

  function addDigit(digit: string) {
    if (pin.length >= maxLength) return
    setError(null)
    setPin(prev => prev + digit)
  }

  function removeDigit() {
    setError(null)
    setPin(prev => prev.slice(0, -1))
  }

  function clearPin() {
    setError(null)
    setPin("")
  }

  async function handlePinLogin() {
    if (pin.length !== 6 || loading) return
    setLoading(true)
    setError(null)

    try {
      const { loginWithPin } = await import("@/app/actions/auth")
      const result = await loginWithPin(pin)

      if ("error" in result) {
        setError(result.error)
        setPin("")
        setLoading(false)
        return
      }

      if (result.success && result.email && result.pin) {
        // Einloggen (Mitarbeiter oder System-User)
        const supabase = (await import("@/lib/supabase/client")).createClient()
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: result.email,
          password: result.pin,
        })

        if (signInError) {
          setError("Login fehlgeschlagen")
          setPin("")
          setLoading(false)
          return
        }

        // System-PIN → spezifische Seite, Mitarbeiter → POS
        const redirect = result.isSystem && result.systemRedirect ? result.systemRedirect : "/pos"
        router.push(redirect)
        router.refresh()
        return
      }

      setError("Unbekannter Fehler")
      setPin("")
    } catch {
      setError("Verbindungsfehler")
      setPin("")
    }
    setLoading(false)
  }

  return (
    <>
    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
      <div className="px-8 pt-8 pb-4 text-center">
        <div className="w-12 h-12 rounded-2xl bg-[#0B3D5E]/10 mx-auto mb-3 flex items-center justify-center">
          <KeyRound size={24} className="text-[#0B3D5E]" />
        </div>
        <p className="text-sm text-gray-500">PIN eingeben</p>
      </div>

      {/* PIN Dots */}
      <div className="flex justify-center gap-3 px-8 pb-4">
        {Array.from({ length: maxLength }).map((_, i) => (
          <div
            key={i}
            className={`w-4 h-4 rounded-full transition-all ${
              i < pin.length
                ? "bg-[#0B3D5E] scale-110"
                : "bg-gray-200"
            }`}
          />
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="px-8 pb-3">
          <p className="text-sm text-red-500 text-center">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center pb-3">
          <Loader2 size={20} className="animate-spin text-[#0B3D5E]" />
        </div>
      )}

      {/* Number Pad */}
      <div className="px-8 pb-6">
        <div className="grid grid-cols-3 gap-2">
          {["7", "8", "9", "4", "5", "6", "1", "2", "3"].map(d => (
            <button
              key={d}
              onClick={() => addDigit(d)}
              disabled={loading}
              className="h-14 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-xl font-bold text-[#0B3D5E] transition-all disabled:opacity-50"
            >
              {d}
            </button>
          ))}
          <button
            onClick={clearPin}
            disabled={loading}
            className="h-14 rounded-xl bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-400 transition-all disabled:opacity-50"
          >
            Löschen
          </button>
          <button
            onClick={() => addDigit("0")}
            disabled={loading}
            className="h-14 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-xl font-bold text-[#0B3D5E] transition-all disabled:opacity-50"
          >
            0
          </button>
          <button
            onClick={removeDigit}
            disabled={loading}
            className="h-14 rounded-xl bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-400 transition-all disabled:opacity-50"
          >
            <Delete size={20} />
          </button>
        </div>
      </div>

      {/* Switch to email */}
      <div className="px-8 pb-6 text-center">
        <button
          onClick={onSwitchToEmail}
          className="text-xs text-gray-400 hover:text-[#0B3D5E] transition-colors"
        >
          Mit E-Mail anmelden
        </button>
      </div>
    </div>

    {/* Neu starten / Ausschalten + Version */}
    <div className="mt-5 space-y-3">
      <div className="flex gap-2">
        <button
          onClick={() => {
            fetch("http://localhost:3001/system/reboot", { method: "POST" }).catch(() => {})
          }}
          className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-white/10 text-white/60 text-xs font-medium hover:bg-white/20 hover:text-white transition-colors"
        >
          <RotateCcw size={13} />
          Neu starten
        </button>
        <button
          onClick={() => {
            fetch("http://localhost:3001/system/shutdown", { method: "POST" }).catch(() => {})
          }}
          className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-white/10 text-white/60 text-xs font-medium hover:bg-red-500/30 hover:text-white transition-colors"
        >
          <Power size={13} />
          Ausschalten
        </button>
      </div>
      <p className="text-center text-[10px] text-white/30 font-mono">VUX-CRM v{APP_VERSION}</p>
    </div>
    </>
  )
}

// ── Email Login ──────────────────────────────────────────────────────────────

function EmailLogin({ onSwitchToPin }: { onSwitchToPin: () => void }) {
  const router = useRouter()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const supabase = createClient()
    const { error } = await supabase.auth.signInWithPassword({ email, password })

    if (error) {
      setError("E-Mail oder Passwort falsch.")
      setLoading(false)
      return
    }

    // Admins sind standortübergreifend (keine Kasse/Dashboard) → auf Aufträge leiten
    let target = "/pos"
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      const { data: u } = await supabase.from("users").select("role").eq("auth_id", user.id).maybeSingle()
      if (u?.role === "admin") target = "/orders"
    }

    router.push(target)
    router.refresh()
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
      <div className="px-8 pt-8 pb-4 text-center">
        <p className="text-sm text-gray-500">Mit E-Mail anmelden</p>
      </div>

      <form onSubmit={handleLogin} className="px-8 pb-6 flex flex-col gap-3">
        <input
          type="email"
          placeholder="E-Mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          className="w-full h-12 rounded-xl border border-gray-200 px-4 text-sm outline-none focus:border-[#0B3D5E] transition-colors"
        />
        <input
          type="password"
          placeholder="Passwort"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          className="w-full h-12 rounded-xl border border-gray-200 px-4 text-sm outline-none focus:border-[#0B3D5E] transition-colors"
        />
        {error && <p className="text-sm text-red-500 text-center">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full h-12 rounded-xl bg-[#0B3D5E] text-white text-sm font-semibold hover:bg-[#0a3452] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          Anmelden
        </button>
      </form>

      <div className="px-8 pb-6 text-center">
        <button
          onClick={onSwitchToPin}
          className="text-xs text-gray-400 hover:text-[#0B3D5E] transition-colors"
        >
          Mit PIN anmelden
        </button>
      </div>
    </div>
  )
}
